<template>
  <v-form>
    <layout-group
      v-if="hasLayoutSetting"
      :layout="consolidatedValues[ddbEntry.deviceClass + '/input/layout']"
      :initial="consolidatedValues[ddbEntry.deviceClass + '/input/initial']"
      :max="consolidatedValues[ddbEntry.deviceClass + '/input/max']"
      :soft-max="consolidatedValues[ddbEntry.deviceClass + '/input/soft_max']"
      :amounts="Object.values(consolidatedValues).filter(s => ['firstValue', 'secondValue', 'thirdValue'].includes(s.type))"
      :currency="currentOrganization.currency"
      @input="({ key, value }) => $set(localValues, key, value)"
      @clear="key => clearSingle(key)"
      @set-here="key => setHere(key)"
    />
    <hardware-amount-group
      v-if="hasHardwareAmountGroup"
      :initial="consolidatedValues[ddbEntry.deviceClass + '/input/initial']"
      :max="consolidatedValues[ddbEntry.deviceClass + '/input/max']"
      :collection-count="consolidatedValues[ddbEntry.deviceClass + '/display/collection_count'] || null"
      :currency="currentOrganization.currency"
      @input="({ key, value }) => $set(localValues, key, value)"
      @clear="key => clearSingle(key)"
      @set-here="key => setHere(key)"
    />
    <appearance-group
      v-if="hasAppearanceGroup"
      :background-image="consolidatedValues['assets/' + ddbEntry.deviceClass + '/background/image']"
      :free-color="consolidatedValues[ddbEntry.deviceClass + '/primary_color']"
      @input="({ key, value }) => $set(localValues, key, value)"
      @clear="key => clearSingle(key)"
      @set-here="key => setHere(key)"
      @upload="uploadFile"
    />
    <logo-group
      v-if="logos.length"
      :logos="logos"
      :logo-is-text="consolidatedValues[ddbEntry.deviceClass + '/main_logo_is_text']"
      @input="({ key, value }) => $set(localValues, key, value)"
      @clear="key => clearSingle(key)"
      @set-here="key => setHere(key)"
      @upload="uploadFile"
    />
    <led-ring-group
      v-if="hasLedRingGroup"
      :color="consolidatedValues[ddbEntry.deviceClass + '/ledring/data/color']"
      :mode="consolidatedValues[ddbEntry.deviceClass + '/ledring/mode']"
      @input="({ key, value }) => $set(localValues, key, value)"
      @clear="key => clearSingle(key)"
      @set-here="key => setHere(key)"
    />
    <confirmation-group
      v-if="hasConfirmationGroup"
      :min-amount="consolidatedValues[ddbEntry.deviceClass + '/confirmation/min_amount']"
      :contact-email="consolidatedValues[ddbEntry.deviceClass + '/confirmation/contact_email'] || null"
      @input="({ key, value }) => $set(localValues, key, value)"
      @clear="key => clearSingle(key)"
      @set-here="key => setHere(key)"
    />
    <legal-group
      v-if="hasLegalGroup"
      :privacy-url="consolidatedValues[ddbEntry.deviceClass + '/privacy_url']"
      :imprint-url="consolidatedValues[ddbEntry.deviceClass + '/imprint_url']"
      @input="({ key, value }) => $set(localValues, key, value)"
      @clear="key => clearSingle(key)"
      @set-here="key => setHere(key)"
    />
    <thank-you-group
      v-if="hasThankYouGroup"
      :thank-you-text="consolidatedValues[ddbEntry.deviceClass + '/thank_you/text']"
      :share-text="ddbEntry.deviceClass === 'WS' ? consolidatedValues[ddbEntry.deviceClass + '/thank_you_share/text'] : null"
      :social-media="ddbEntry.deviceClass === 'WS' ? consolidatedValues[ddbEntry.deviceClass + '/social_media'] : null"
      @input="({ key, value }) => $set(localValues, key, value)"
      @clear="key => clearSingle(key)"
      @set-here="key => setHere(key)"
    />
    <template v-for="s in Object.values(consolidatedValues).filter(s => !isHandledByGroupComponent(s))">
      <single-setting
        :key="s.key"
        v-model="localValues[s.key]"
        :setting="consolidatedValues[s.key]"
        :all-settings="consolidatedValues"
        @clear="clearSingle(s.key)"
        @set-here="setHere(s.key)"
        @upload="uploadFile"
        :device-class="ddbEntry.deviceClass"
      />
    </template>
    <template v-if="ddbEntry.hardware || ddbEntry.deviceClass === 'WT'">
      <!-- FIXME When changing location here also update the settings stack above -->
      <h3 id="change-location-anchor">Ort zuweisen</h3>
      <v-card>
        <v-card-text>
          <v-row>
            <v-col cols="12">
              <p>Indem Sie das Gerät einem Ort zuweisen, übernimmt es die Sammlungspläne des Ortes und der
                übergeordneten Organisationen, sowie ggbf. weitere dort definierte Einstellungen.
                Wenn Sie den Ort ändern, wird das Gerät einen Kassenschnitt im Kontext des bisherigen Ortes durchführen
                und dann die Einstellungen für den neuen Ort übernehmen.
              </p>
            </v-col>
            <v-col cols="12">
              <v-autocomplete
                v-model="locationSelect"
                :items="locationItems"
                :prepend-icon="mdiCrosshairsGps"
                autocomplete="off"
              ></v-autocomplete>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </template>
  </v-form>
</template>

<script>
import { settingsForDeviceType } from '@/lib/device-db'
import SingleSetting from '@/components/device/SingleSetting'
import LayoutGroup from '@/components/device/LayoutGroup'
import HardwareAmountGroup from '@/components/device/HardwareAmountGroup'
import AppearanceGroup from '@/components/device/AppearanceGroup'
import LogoGroup from '@/components/device/LogoGroup'
import LegalGroup from '@/components/device/LegalGroup'
import ConfirmationGroup from '@/components/device/ConfirmationGroup'
import LedRingGroup from '@/components/device/LedRingGroup'
import ThankYouGroup from '@/components/device/ThankYouGroup'
import {
  FETCH_CONTENTIMAGES,
  FETCH_LOCATIONS,
  FETCH_ORGANIZATIONS,
  PATCH_DEVICE,
  PATCH_ORGANIZATION
} from '@/store/action-types'
import { INVALIDATE_CACHE } from '@/modules/common/store/action-types'
import { SET_SNACKBAR_MESSAGE } from '@/modules/common/store/mutation-types'
import { mapGetters, mapState } from 'vuex'
import { shortenParish } from '@/lib/regex-tools'
import { mdiCrosshairsGps } from '@mdi/js'
import { erectObj } from '@/lib/settings-tools'
import { CACHE_CONTENTIMAGES } from '@/store/cache-types'

// Single source of truth for which group component owns which setting types, and for
// which device classes that group applies to. hasXGroup computed properties (driving
// the template's v-if gates) and isHandledByGroupComponent (deciding what falls back to
// SingleSetting) both read from this table instead of maintaining their own copies.
const LAYOUT_DEVICE_CLASSES = ['WS', 'FS', 'WT']
const APPEARANCE_DEVICE_CLASSES = ['WS', 'FS', 'WT']
const LEGAL_DEVICE_CLASSES = ['WS']
const CONFIRMATION_DEVICE_CLASSES = ['SP', 'SO', 'KS']
const THANK_YOU_DEVICE_CLASSES = ['WS', 'FS']

const GROUP_COMPONENTS = {
  layout: {
    isActive: ddbEntry => LAYOUT_DEVICE_CLASSES.includes(ddbEntry.deviceClass),
    types: ['inputLayout', 'initialInputValue', 'maxInputValue', 'softMaxInputValue', 'firstValue', 'secondValue', 'thirdValue'],
  },
  hardwareAmount: {
    isActive: ddbEntry => ddbEntry.hardware && !LAYOUT_DEVICE_CLASSES.includes(ddbEntry.deviceClass),
    types: ['initialInputValue', 'maxInputValue', 'collectionCount'],
  },
  appearance: {
    isActive: ddbEntry => APPEARANCE_DEVICE_CLASSES.includes(ddbEntry.deviceClass),
    types: ['backgroundImage', 'freeColor'],
  },
  logo: {
    isActive: () => true,
    types: ['webappLogo', 'mainLogo', 'cornerLogo', 'spLogo', 'logoIsText'],
  },
  legal: {
    isActive: ddbEntry => LEGAL_DEVICE_CLASSES.includes(ddbEntry.deviceClass),
    types: ['privacyUrl', 'imprintUrl'],
  },
  confirmation: {
    isActive: ddbEntry => ddbEntry.hardware && CONFIRMATION_DEVICE_CLASSES.includes(ddbEntry.deviceClass),
    types: ['confirmationMinAmount', 'confirmationContactEmail'],
  },
  ledRing: {
    isActive: ddbEntry => !!ddbEntry.ledRing,
    types: ['ledringColor', 'ledringMode'],
  },
  thankYou: {
    isActive: ddbEntry => THANK_YOU_DEVICE_CLASSES.includes(ddbEntry.deviceClass),
    types: ['templateTextThankYou', 'templateTextThankYouShare', 'socialMedia'],
  },
}

export default {
  name: 'DeviceSettings',
  components: { SingleSetting, LayoutGroup, HardwareAmountGroup, AppearanceGroup, LogoGroup, LegalGroup, ConfirmationGroup, LedRingGroup, ThankYouGroup },
  props: {
    ddbEntry: Object,
    device: {
      type: Object,
      required: true,
    },
    value: {
      type: Boolean,
      required: false,
      default: false,
    }
  },
  async mounted () {
    const promises = []
    if (this.ddbEntry?.hardware || this.ddbEntry?.deviceClass === 'WT') {
      promises.push(
        this.$store.dispatch('organization/' + FETCH_ORGANIZATIONS),
        this.$store.dispatch('location/' + FETCH_LOCATIONS),
      )
    }
  },
  data: function () {
    return {
      mdiCrosshairsGps,
      localValues: {},
      // eslint-disable-next-line camelcase
      locationSelect: this.device?.current_location
    }
  },
  methods: {
    reset () {
      // eslint-disable-next-line camelcase
      this.locationSelect = this.device?.current_location
      if (this?.device?.url) {
        for (const item of this.availableSettings) {
          this.$set(this.localValues, item.key, undefined)
        }
      }
    },
    clearSingle (itemKey) {
      this.$set(this.localValues, itemKey, null)
    },
    setHere (itemKey) {
      this.$set(this.localValues, itemKey, this.consolidatedValues[itemKey]?.currentValue)
    },
    isHandledByGroupComponent (setting) {
      return Object.values(GROUP_COMPONENTS).some(
        group => group.isActive(this.ddbEntry) && group.types.includes(setting.type)
      )
    },
    async save () {
      const valueListToSave = Object.entries(this.localValues).filter(([key, value]) => value !== undefined)
      // FIXME HACK HACK HACK: Image objects have the form {"image": "hash"} or null.
      //  We should implement a way to handle this natively. Will be necessary once we get to allowing SVG uploads
      const clearObj = erectObj(valueListToSave.map(([key, value]) => [key.replace(/\/image$/, ''), null]))
      const setObj = erectObj(valueListToSave.filter(([key, value]) => value !== null).map(([key, value]) => [key, value]))

      const data = {
        /* For now we'll always set all settings as inherited */
        settings_inherit: setObj,
        settings_local: clearObj,
      }

      const isWebApp = this?.ddbEntry?.webApp && this?.ddbEntry?.deviceClass !== 'WT'
      const url = isWebApp ? this.currentOrganization.url : this.device.url
      const action = isWebApp ? 'organization/' + PATCH_ORGANIZATION : 'device/' + PATCH_DEVICE

      if (this?.ddbEntry?.hardware || this?.ddbEntry?.deviceClass === 'WT') {
        // eslint-disable-next-line camelcase
        if (this.locationSelect !== this.device?.current_location) {
          data.current_location = this.locationSelect
        }
      }

      const maxAmountKey = this.ddbEntry.deviceClass + '/input/max'
      const maxAmountChanged = this.ddbEntry.hardware && valueListToSave.some(([key]) => key === maxAmountKey)

      const result = await this.$store.dispatch(action, { url, data })
      if (maxAmountChanged) {
        this.$store.commit(SET_SNACKBAR_MESSAGE, { message: 'Maximalbetrag geändert – bitte Gerät neu starten, damit die Änderung wirksam wird.' })
      }
      return result
    },
    async uploadFile ({ setting, fileElement, types } = { types: [] }) {
      const formData = new FormData()
      formData.append('file', fileElement)
      for (const type_ of types) {
        formData.append('types', type_)
      }
      formData.append('organization', this.currentOrganization?.url ?? null)

      const result = await this.$store.getters.restApi.post('content/image/', formData)
      const imageObj = result.data
      await this.$store.dispatch(INVALIDATE_CACHE, { key: CACHE_CONTENTIMAGES })
      await this.$store.dispatch('contentimage/' + FETCH_CONTENTIMAGES)
      this.$nextTick(() => this.$set(this.localValues, setting.key, imageObj.hash))
      // FIXME Handle errors
    },
  },
  computed: {
    ...mapGetters('organization', ['allOrganizations', 'currentOrganization']),
    ...mapState('location', ['locations']),
    availableSettings () {
      return settingsForDeviceType(this.device.type).map(
        item => { return { ...item, key: item.key.replace("$DC", this.ddbEntry.deviceClass) } }
      )
    },
    hasLayoutSetting () {
      return GROUP_COMPONENTS.layout.isActive(this.ddbEntry)
    },
    hasHardwareAmountGroup () {
      return GROUP_COMPONENTS.hardwareAmount.isActive(this.ddbEntry)
    },
    hasAppearanceGroup () {
      return GROUP_COMPONENTS.appearance.isActive(this.ddbEntry)
    },
    hasLegalGroup () {
      return GROUP_COMPONENTS.legal.isActive(this.ddbEntry)
    },
    hasConfirmationGroup () {
      return GROUP_COMPONENTS.confirmation.isActive(this.ddbEntry)
    },
    hasLedRingGroup () {
      return GROUP_COMPONENTS.ledRing.isActive(this.ddbEntry)
    },
    hasThankYouGroup () {
      return GROUP_COMPONENTS.thankYou.isActive(this.ddbEntry)
    },
    logos () {
      return Object.values(this.consolidatedValues).filter(s => ['webappLogo', 'mainLogo', 'cornerLogo', 'spLogo'].includes(s.type))
    },
    settingsStack () {
      // FIXME Override with new location?
      if (this.ddbEntry?.webApp && this.ddbEntry?.deviceClass !== 'WT') {
        // FIXME Override with device
        return this.$store.getters['settings/settingsMapStackForUrl'](this?.currentOrganization?.url ?? null)
      } else {
        return this.$store.getters['settings/settingsMapStackForUrl'](this?.device?.url ?? null)
      }
    },
    anySettingChanged () {
      return (
        // eslint-disable-next-line camelcase
        ((this.ddbEntry?.hardware || this.ddbEntry?.deviceClass === 'WT') && this.locationSelect !== this.device?.current_location) ||
        Object.values(this.localValues).some(item => item !== undefined)
      )
    },
    consolidatedValues () {
      const tmp = {}
      for (const [key, stack] of Object.entries(this.settingsStack)) {
        tmp[key] = { }
        if (stack && stack?.length > 0) {
          tmp[key].currentFrame = stack[0]
          if (stack[0]?.source === this.device.url) {
            tmp[key].locallySet = true
          }
          if (stack[0]?.source === this.device.url && stack.length > 1) {
            tmp[key].nextFrame = stack[1]
          } else {
            tmp[key].nextFrame = undefined
          }

          if (key.includes('WS/social_media') && !key.endsWith('social_media')) {
            tmp['WS/social_media'].currentFrame.value = {
              ...tmp['WS/social_media'].currentFrame.value,
              [key.split('/')[2]]: tmp[key].currentFrame.value,
            }
          }
        } else {
          tmp[key].nextFrame = tmp[key].currentFrame = undefined
        }
      }
      const retval = {}
      for (const item of this.availableSettings) {
        retval[item.key] = {
          locallySet: false,
          ...item,
          ...tmp[item.key],
        }
        if (retval[item.key]?.nextFrame === undefined) {
          retval[item.key].nextFrame = {
            inherit: true,
            source: undefined,
            value: item.defaultValue ?? null,
          }
        }
        if (retval[item.key]?.currentFrame === undefined) {
          retval[item.key].currentFrame = retval[item.key].nextFrame
        }
        if (Object.prototype.hasOwnProperty.call(this.localValues, item.key) && this.localValues[item.key] !== undefined) {
          retval[item.key].currentValue = this.localValues[item.key]
          retval[item.key].locallySet = true
        }
        if (retval[item.key]?.currentValue === undefined) {
          retval[item.key].currentValue = retval[item.key]?.currentFrame?.value
        } else if (retval[item.key]?.currentValue === null) {
          retval[item.key].currentValue = retval[item.key]?.nextFrame?.value
          retval[item.key].locallySet = false
        }
      }
      return retval
    },
    locationItems () {
      // FIXME There should be some restriction here
      const retval = []
      const findParents = (current) => {
        const retval = []
        while (current) {
          retval.unshift(current)
          current = this.allOrganizations[current]?.parent
        }
        return retval
      }
      for (const location of this.locations) {
        if (!location?.name) {
          continue
        }
        retval.push({
          location,
          parents: findParents(location.organization),
          value: location.url,
        })
      }

      let commonPrefix = 0
      if (retval.length) {
        commonPrefix = retval[0].parents.length
        for (const [index, value] of retval[0].parents.entries()) {
          if (retval.some(item => item.parents[index] !== value)) {
            commonPrefix = index
            break
          }
        }
      }
      if (commonPrefix > 0) { /* Show at least one organisation. FIXME: Make this smarter if already all prefixes include one organisation */
        commonPrefix--
      }

      for (const item of retval) {
        const orgNames = item.parents.slice(commonPrefix).map(org => {
          const organization = this.allOrganizations[org]
          return organization ? shortenParish(organization.name) : null
        }).filter(name => !!name)

        item.text = [
          ...(commonPrefix > 0 ? ["..."] : []),
          ...orgNames,
          item.location.name
        ].join(" / ")
      }

      retval.sort((a, b) => a.text.localeCompare(b.text))

      return retval
    }
  },
  watch: {
    device: {
      immediate: true,
      handler (newVal, oldVal) {
        if (
          oldVal &&
          (newVal?.id !== oldVal?.id)
        ) {
          this.$nextTick(this.reset)
        }
      }
    },
    anySettingChanged: {
      immediate: true,
      handler (newVal) {
        this.$emit('input', newVal)
      }
    }
  }
}
</script>

<style scoped>

</style>
